import { ArrowLeft, Calendar, ChevronDown, ChevronUp, Copy, Download, RefreshCw, Send } from "lucide-react";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import ApplicantAnalysisResult from "./ApplicantAnalysisResult";
import { ResumePreviewPanel } from "./ResumeTabContent";
import { toast } from "sonner";
import {
  useApplicantsAutoUpdate,
  useAnalysisAutoUpdate,
} from "../../hooks/useAutoUpdate";
import {
  CLOSE_INTERVIEW,
  REOPEN_INTERVIEW,
  SEND_CALENDAR_LINK,
  SEND_CALENDAR_REMINDER,
  RESEND_INTERVIEW,
  REJECT_CANDIDATES,
  RETRY_ANALYSIS,
} from "../../lib/urls";
import {
  fetchTranscriptsByCandidateId,
  mergePreservedTranscript,
} from "../../utils/transcriptUtils";
import { useCandidateTranscript } from "../../hooks/useCandidateTranscript";
import {
  RETRY_ANALYSIS_BULK_LIMIT,
  canRetryAnalysis,
  canRetryMoveAtsStages,
  formatBulkRetryToast,
  formatPartialRetryToast,
  getApiErrorMessage,
  getAtsStageUpdateDisabledReason,
  getRetryAnalysisDisabledReason,
  isAnalysisRetryEligible,
  isAnalysisRetryLocked,
  renderRetryProgressToast,
  waitForRetryAnalysesToFinish,
} from "../../utils/retryAnalysisUtils";
import {
  applicantStageIsOutsideEligible,
  stageLooksTerminal,
} from "../../utils/applicantsStageFilter";
import {
  clearApplicantsListFilters,
  mergeOptionSetFilter,
  readApplicantsListFilters,
  writeApplicantsListFilters,
} from "../../utils/applicantsListFilterStorage";

import UploadApplicantsModal from "./UploadApplicantsModal";
import RejectApplicantModal from "./RejectApplicantModal";
import MultipleApplicationsModal from "./MultipleApplicationsModal";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { isAnalysisRubricRequiredDetail } from "../../utils/analysisRubricPreflight";
import { getModalConfig } from "../ConfirmationModal/modalHelper";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";
import RoleNotSetUpEmptyState from "../JobManagement/RoleNotSetUpEmptyState";

import {
  SmallText,
  XsmallText,
} from "../../lib/ui/components/typography/Typography";
import Checkbox from "../../lib/ui/components/checkbox/Checkbox";
import Button from "../../lib/ui/components/button/Button";
import FiltersButton from "../../lib/ui/components/filters-button/FiltersButton";
import SearchInput from "../../lib/ui/components/input/SearchInput";
import Dropdown from "../../lib/ui/components/dropdown/Dropdown";
import Table from "../../lib/ui/components/table/Table";
import Badge from "../../lib/ui/components/badge/Badge";
import MatchRating from "../../lib/ui/components/match-rating/MatchRating";
import Link from "../../lib/ui/components/link/Link";
import Spinner from "../../lib/ui/components/spinner/Spinner";
import OutdatedQuestionsBadge from "../../lib/ui/components/outdated-questions-badge/OutdatedQuestionsBadge";
import {
  getOverallRatingDisplay,
  getRequiredQuestionsLabel,
} from "../../utils/analysisDisplay";
import Modal from "../../lib/ui/components/Modal";
import SidePanel from "../../lib/ui/components/SidePanel";
import Tooltip from "../../lib/ui/components/tooltip/Tooltip";


import { isRejectSupported } from "../../lib/constants";

import { calculatePageAfterDeletion } from "../../utils/tableUtils";
import { useTablePagination } from "../../hooks/useTablePagination";
import { useStore } from "../../store";
import { isDevEnvironment } from "../../utils/envUtils";
import {
  formatStatus,
  getDisplayStatus,
  statusFilterOptions,
} from "./StatusHelper";
import {
  atsApplicationCount,
  toAnalysisApplicant,
} from "./atsApplications";
import { formatPhoneNumber } from "../../utils/phoneUtils";
import { bulkExportAnalysisToPDF } from "./ExportAnalysisHelper";

// Interview action types
const INTERVIEW_ACTION_TYPES = {
  CLOSE: "close",
  REOPEN: "reopen",
};

const DOWNLOAD_LIMIT = 20;

/** Sentinel for applicants with no ATS stage (must not collide with real stage names). */
const STAGE_FILTER_NA = "__STAGE_NA__";
/** Sentinel for applicants with no location state (must not collide with real state names). */
const STATE_FILTER_NA = "__STATE_NA__";
const APPLICANTS_TAB_POLL_INTERVAL_MS = 5 * 60 * 1000;

const APPLICANT_TABLE_PAGINATION_OPTIONS = {
  itemsPerPage: 10,
  defaultSortField: "match_rating",
  defaultSortDirection: "desc",
  sortFieldConfig: {
    updated_at: {
      type: "date",
      getValue: (item) => item?.interview_session?.updated_at,
      nullHandling: "bottom",
      fallback: (a, b) => {
        const aUpdated = new Date(a.updated_at || 0);
        const bUpdated = new Date(b.updated_at || 0);
        const timeDiff = bUpdated - aUpdated;
        if (timeDiff !== 0) return timeDiff;
        const aName = (a.name || "").toLowerCase();
        const bName = (b.name || "").toLowerCase();
        return aName.localeCompare(bName);
      },
    },
    created_at: {
      type: "date",
      getValue: (item) => item?.created_at,
      nullHandling: "bottom",
      fallback: (a, b) => {
        const aCreated = new Date(a.created_at || 0);
        const bCreated = new Date(b.created_at || 0);
        const timeDiff = bCreated - aCreated;
        if (timeDiff !== 0) return timeDiff;
        const aName = (a.name || "").toLowerCase();
        const bName = (b.name || "").toLowerCase();
        return aName.localeCompare(bName);
      },
    },
    match_rating: {
      type: "number",
      getValue: (item) =>
        item?.interview_session?.ai_insights?.overall_match_rating,
      nullHandling: "bottom",
      fallback: (a, b) => {
        const aUpdated = new Date(
          a?.interview_session?.updated_at || a.updated_at
        );
        const bUpdated = new Date(
          b?.interview_session?.updated_at || b.updated_at
        );
        const timeDiff = bUpdated - aUpdated;
        if (timeDiff !== 0) return timeDiff;
        const aName = (a.name || "").toLowerCase();
        const bName = (b.name || "").toLowerCase();
        return aName.localeCompare(bName);
      },
    },
    required_questions_status: {
      type: "string",
      getValue: (item) =>
        getRequiredQuestionsLabel(
          item?.interview_session?.ai_insights?.required_questions_status
        ),
      nullHandling: "bottom",
      fallback: (a, b) => {
        const aUpdated = new Date(
          a?.interview_session?.updated_at || a.updated_at
        );
        const bUpdated = new Date(
          b?.interview_session?.updated_at || b.updated_at
        );
        return bUpdated - aUpdated;
      },
    },
    name: {
      type: "string",
      getValue: (item) => item.name,
      fallback: (a, b) => {
        const aName = (a.name || "").toLowerCase();
        const bName = (b.name || "").toLowerCase();
        return aName.localeCompare(bName);
      },
    },
  },
  defaultSorts: {
    updated_at: "desc",
    created_at: "desc",
    match_rating: "desc",
    name: "asc",
  },
};

/** Drawer-only option lists (aligned with prior dropdown filters). */
const DRAWER_MATCH_RATING_OPTIONS = [
  { value: 5, label: "5" },
  { value: 4, label: "4" },
  { value: 3, label: "3" },
  { value: 2, label: "2" },
  { value: 1, label: "1" },
  { value: "No Rating", label: "No Rating" },
];

const DRAWER_REQUIRED_QUESTIONS_OPTIONS = [
  { value: "MET", label: "Passed" },
  { value: "FAILED", label: "Failed" },
  { value: "UNRESOLVED", label: "Needs review" },
  { value: "NA", label: "N/A" },
];

/** Collapsible filter block for the applicants drawer (no border/card chrome). */
function ApplicantFiltersDrawerSection({
  title,
  children,
  defaultOpen = true,
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="w-full">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 bg-transparent py-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 rounded dark:focus-visible:ring-offset-gray-800"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <XsmallText weight="bold">{title}</XsmallText>
        <ChevronDown
          className={`h-3 w-3 shrink-0 text-gray-600 transition-transform duration-200 dark:text-gray-400 ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open ? (
        <div className="flex flex-col gap-2 pt-2">{children}</div>
      ) : null}
    </div>
  );
}

function isDefaultMatchRatingFilter(set) {
  return (
    set.size === 6 &&
    set.has(1) &&
    set.has(2) &&
    set.has(3) &&
    set.has(4) &&
    set.has(5) &&
    set.has("No Rating")
  );
}

/** Initial Pass/Fail selection: Failed is unchecked, so failed applicants start hidden. */
const INITIAL_REQUIRED_QUESTIONS_FILTER = ["MET", "UNRESOLVED", "NA"];
/** Every Pass/Fail status — selecting all of these means the filter is doing nothing. */
const ALL_REQUIRED_QUESTIONS_STATUSES = [
  "MET",
  "PARTIALLY_MET",
  "NOT_MET",
  "UNRESOLVED",
  "NA",
];

function isUnfilteredRequiredQuestions(set) {
  return (
    set.size === ALL_REQUIRED_QUESTIONS_STATUSES.length &&
    ALL_REQUIRED_QUESTIONS_STATUSES.every((value) => set.has(value))
  );
}

function areSetsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

function buildDefaultStageFilterSet(stageFilterOptionValues) {
  return new Set(stageFilterOptionValues);
}

export default function ApplicantsTab({
  companyId,
  jobId,
  applicants = [],
  onCandidatesUpdate,
  onCandidatesUpdateSilent,
  token,
  status,
  needsSetup = false,
  onSetUpRole,
  shortId,
  loading = false,
  roleName = "",
  companyName = "",
  targetSessionId = null,
  showApplicantNavigation = true,
  jobStageSettings = null,
  hasAnalysisRubric = false,
  onAnalysisRubricRequired,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const devModeEnabled = useStore((state) => state.devModeEnabled);
  const company = useStore((state) => state.company);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSendingCalendar, setIsSendingCalendar] = useState(false);
  const [isSendingReminder, setIsSendingReminder] = useState(false);

  const isJobClosed = status !== "OPEN";
  const roleNeedsSetup = needsSetup;
  const rejectSupported = isRejectSupported(company?.ats_integration_id);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);

  // Bulk delete state
  const [selectedApplicants, setSelectedApplicants] = useState(new Set());
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  // Send interview modal state
  const [sendInterviewModalOpen, setSendInterviewModalOpen] = useState(false);
  const [candidateToSendInterview, setCandidateToSendInterview] =
    useState(null);

  // Resend interview modal state
  const [resendInterviewModalOpen, setResendInterviewModalOpen] =
    useState(false);
  const [candidateToResendInterview, setCandidateToResendInterview] =
    useState(null);
  const [retryAnalysisModalOpen, setRetryAnalysisModalOpen] = useState(false);
  const [retryAnalysisSessionIds, setRetryAnalysisSessionIds] = useState([]);

  // Bulk send interview state
  const [selectedInterviewCandidates, setSelectedInterviewCandidates] =
    useState(new Set());

  // Status filter state
  const [statusFilter, setStatusFilter] = useState("all");
  // Match rating filter state - Set of selected ratings (default: all ratings selected including No Rating)
  const [matchRatingFilter, setMatchRatingFilter] = useState(
    new Set([1, 2, 3, 4, 5, "No Rating"])
  );
  // Required questions filter — Failed unchecked on first load
  const [requiredQuestionsFilter, setRequiredQuestionsFilter] = useState(
    () => new Set(INITIAL_REQUIRED_QUESTIONS_FILTER)
  );
  const [stageFilter, setStageFilter] = useState(() => new Set());
  /** When true, empty stage selection means "show none"; when false, empty means "show all" (initial). */
  const [stageUserClearedAll, setStageUserClearedAll] = useState(false);
  const [stateFilter, setStateFilter] = useState(() => new Set());
  const [stateUserClearedAll, setStateUserClearedAll] = useState(false);
  const [keywordSearchQuery, setKeywordSearchQuery] = useState("");
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);

  const [showRejectedSection, setShowRejectedSection] = useState(false);
  const [deleteFromRejected, setDeleteFromRejected] = useState(false);

  // Reject modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [isBulkReject, setIsBulkReject] = useState(false);
  const [candidateToReject, setCandidateToReject] = useState(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [applicationsModalApplicant, setApplicationsModalApplicant] = useState(null);

  const rejectedApplicants = useMemo(
    () => (applicants || []).filter((a) => a.status === "ATS_DECLINED"),
    [applicants]
  );

  const applicantsForMain = useMemo(
    () => (applicants || []).filter((a) => a.status !== "ATS_DECLINED"),
    [applicants]
  );

  const stageFilterOptionValues = useMemo(() => {
    const seen = new Set();
    for (const a of applicantsForMain || []) {
      const raw = a?.ats_current_stage_name;
      if (typeof raw === "string" && raw.trim()) {
        seen.add(raw.trim());
      } else {
        seen.add(STAGE_FILTER_NA);
      }
    }
    const arr = Array.from(seen);
    arr.sort((a, b) => {
      if (a === STAGE_FILTER_NA) return 1;
      if (b === STAGE_FILTER_NA) return -1;
      return a.localeCompare(b, undefined, { sensitivity: "base" });
    });
    return arr;
  }, [applicantsForMain]);

  const stageFilterOptions = useMemo(
    () =>
      stageFilterOptionValues.map((v) => ({
        value: v,
        label: v === STAGE_FILTER_NA ? "N/A" : v,
      })),
    [stageFilterOptionValues]
  );

  const defaultStageFilter = useMemo(
    () => buildDefaultStageFilterSet(stageFilterOptionValues),
    [stageFilterOptionValues]
  );

  const stateFilterOptionValues = useMemo(() => {
    const seen = new Set();
    for (const a of applicantsForMain || []) {
      const raw = a?.location?.state;
      if (typeof raw === "string" && raw.trim()) {
        seen.add(raw.trim());
      } else {
        seen.add(STATE_FILTER_NA);
      }
    }
    const arr = Array.from(seen);
    arr.sort((a, b) => {
      if (a === STATE_FILTER_NA) return 1;
      if (b === STATE_FILTER_NA) return -1;
      return a.localeCompare(b, undefined, { sensitivity: "base" });
    });
    return arr;
  }, [applicantsForMain]);

  const stateFilterOptions = useMemo(
    () =>
      stateFilterOptionValues.map((v) => ({
        value: v,
        label: v === STATE_FILTER_NA ? "N/A" : v,
      })),
    [stateFilterOptionValues]
  );

  const defaultStateFilter = useMemo(
    () => new Set(stateFilterOptionValues),
    [stateFilterOptionValues]
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== "all") count += 1;
    if (!isDefaultMatchRatingFilter(matchRatingFilter)) count += 1;
    if (!isUnfilteredRequiredQuestions(requiredQuestionsFilter)) count += 1;
    if (stageFilterOptionValues.length > 0) {
      if (stageUserClearedAll) {
        count += 1;
      } else {
        const currentStageSelection =
          stageFilter.size > 0 ? stageFilter : defaultStageFilter;
        if (!areSetsEqual(currentStageSelection, defaultStageFilter)) {
          count += 1;
        }
      }
    }
    if (stateFilterOptionValues.length > 0) {
      if (stateUserClearedAll) {
        count += 1;
      } else {
        const currentStateSelection =
          stateFilter.size > 0 ? stateFilter : defaultStateFilter;
        if (!areSetsEqual(currentStateSelection, defaultStateFilter)) {
          count += 1;
        }
      }
    }
    return count;
  }, [
    statusFilter,
    matchRatingFilter,
    requiredQuestionsFilter,
    stageUserClearedAll,
    stageFilter,
    stageFilterOptionValues.length,
    defaultStageFilter,
    stateUserClearedAll,
    stateFilter,
    stateFilterOptionValues.length,
    defaultStateFilter,
  ]);

  const clearAllFilters = useCallback(() => {
    clearApplicantsListFilters(jobId);
    setStatusFilter("all");
    setMatchRatingFilter(new Set([1, 2, 3, 4, 5, "No Rating"]));
    setRequiredQuestionsFilter(new Set(ALL_REQUIRED_QUESTIONS_STATUSES));
    setStageUserClearedAll(false);
    setStageFilter(new Set(defaultStageFilter));
    setStateUserClearedAll(false);
    setStateFilter(new Set(defaultStateFilter));
    setKeywordSearchQuery("");
    setShowRejectedSection(false);
  }, [defaultStageFilter, defaultStateFilter, jobId]);

  const effectiveStageFilter = useMemo(() => {
    if (stageFilterOptionValues.length === 0) return null;
    if (stageUserClearedAll) return new Set();
    if (stageFilter.size > 0) return stageFilter;
    return defaultStageFilter;
  }, [stageFilter, stageUserClearedAll, stageFilterOptionValues, defaultStageFilter]);

  const handleStageFilterChange = useCallback((next) => {
    setStageFilter(next);
    setStageUserClearedAll(next.size === 0);
  }, []);

  const effectiveStateFilter = useMemo(() => {
    if (stateFilterOptionValues.length === 0) return null;
    if (stateUserClearedAll) return new Set();
    if (stateFilter.size > 0) return stateFilter;
    return defaultStateFilter;
  }, [stateFilter, stateUserClearedAll, stateFilterOptionValues, defaultStateFilter]);

  const handleStateFilterChange = useCallback((next) => {
    setStateFilter(next);
    setStateUserClearedAll(next.size === 0);
  }, []);

  const drawerStatusRadioOptions = useMemo(
    () => statusFilterOptions.filter((o) => o.value !== "ats_declined"),
    []
  );

  const toggleStageDrawerOption = useCallback(
    (optionValue, checked) => {
      const fullSet = new Set(stageFilterOptionValues);
      let current;
      if (stageUserClearedAll) {
        current = new Set();
      } else if (stageFilter.size > 0) {
        current = new Set(stageFilter);
      } else {
        current = new Set(defaultStageFilter);
      }

      if (checked) {
        current.add(optionValue);
      } else {
        current.delete(optionValue);
      }

      if (current.size === 0) {
        handleStageFilterChange(new Set());
        return;
      }

      if (
        fullSet.size > 0 &&
        current.size === fullSet.size &&
        stageFilterOptionValues.every((k) => current.has(k))
      ) {
        setStageFilter(new Set(fullSet));
        setStageUserClearedAll(false);
        return;
      }

      setStageFilter(current);
      setStageUserClearedAll(false);
    },
    [
      stageFilter,
      stageUserClearedAll,
      stageFilterOptionValues,
      defaultStageFilter,
      handleStageFilterChange,
    ]
  );

  const toggleStateDrawerOption = useCallback(
    (optionValue, checked) => {
      const fullSet = new Set(stateFilterOptionValues);
      let current;
      if (stateUserClearedAll) {
        current = new Set();
      } else if (stateFilter.size > 0) {
        current = new Set(stateFilter);
      } else {
        current = new Set(defaultStateFilter);
      }

      if (checked) {
        current.add(optionValue);
      } else {
        current.delete(optionValue);
      }

      if (current.size === 0) {
        handleStateFilterChange(new Set());
        return;
      }

      if (
        fullSet.size > 0 &&
        current.size === fullSet.size &&
        stateFilterOptionValues.every((k) => current.has(k))
      ) {
        setStateFilter(new Set(fullSet));
        setStateUserClearedAll(false);
        return;
      }

      setStateFilter(current);
      setStateUserClearedAll(false);
    },
    [
      stateFilter,
      stateUserClearedAll,
      stateFilterOptionValues,
      defaultStateFilter,
      handleStateFilterChange,
    ]
  );

  const toggleMatchRatingDrawerOption = useCallback(
    (value, checked) => {
      const next = new Set(matchRatingFilter);
      if (checked) next.add(value);
      else next.delete(value);
      setMatchRatingFilter(next);
    },
    [matchRatingFilter]
  );

  const toggleRequiredQuestionsDrawerOption = useCallback(
    (value, checked) => {
      const next = new Set(requiredQuestionsFilter);
      if (value === "FAILED") {
        if (checked) {
          next.add("PARTIALLY_MET");
          next.add("NOT_MET");
        } else {
          next.delete("PARTIALLY_MET");
          next.delete("NOT_MET");
        }
      } else {
        if (checked) next.add(value);
        else next.delete(value);
      }
      setRequiredQuestionsFilter(next);
    },
    [requiredQuestionsFilter]
  );

  const savedFiltersRef = useRef(null);
  const filtersPersistReadyRef = useRef(false);
  const restoredTablePrefsRef = useRef(false);

  const stageFilterOptionKey = stageFilterOptionValues.join("\0");
  const stateFilterOptionKey = stateFilterOptionValues.join("\0");

  useEffect(() => {
    filtersPersistReadyRef.current = false;
    restoredTablePrefsRef.current = false;
    savedFiltersRef.current = readApplicantsListFilters(jobId);
    const saved = savedFiltersRef.current;
    if (!saved) {
      return;
    }
    setStatusFilter(saved.statusFilter);
    setKeywordSearchQuery(saved.keywordSearchQuery);
    setMatchRatingFilter(new Set(saved.matchRatingFilter));
    setRequiredQuestionsFilter(new Set(saved.requiredQuestionsFilter));
    setShowRejectedSection(saved.showRejectedSection);
  }, [jobId]);

  useEffect(() => {
    const saved = savedFiltersRef.current;
    if (saved?.stage && stageFilterOptionValues.length > 0) {
      setStageUserClearedAll(saved.stage.stageUserClearedAll);
      setStageFilter(
        mergeOptionSetFilter(
          saved.stage,
          stageFilterOptionValues,
          saved.stage.stageUserClearedAll,
          saved.stage.knownOptions
        )
      );
    } else if (!saved) {
      setStageUserClearedAll(false);
      setStageFilter(new Set(defaultStageFilter));
    }

    if (saved?.state && stateFilterOptionValues.length > 0) {
      setStateUserClearedAll(saved.state.stateUserClearedAll);
      setStateFilter(
        mergeOptionSetFilter(
          saved.state,
          stateFilterOptionValues,
          saved.state.stateUserClearedAll,
          saved.state.knownOptions
        )
      );
    } else if (!saved) {
      setStateUserClearedAll(false);
      setStateFilter(new Set(defaultStateFilter));
    }

    filtersPersistReadyRef.current = true;
  }, [
    jobId,
    stageFilterOptionKey,
    stateFilterOptionKey,
    defaultStageFilter,
    defaultStateFilter,
    stageFilterOptionValues,
    stateFilterOptionValues,
  ]);

  const filteredApplicants = useMemo(() => {
    let filtered = applicantsForMain;

    if (keywordSearchQuery.trim()) {
      const query = keywordSearchQuery.toLowerCase().trim();
      filtered = filtered.filter((applicant) => {
        const loc = applicant?.location;
        const haystack = [
          applicant.name,
          loc?.city,
          loc?.state,
          applicant?.ats_current_stage_name,
        ]
          .filter((value) => typeof value === "string" && value.trim())
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      });
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((applicant) => {
        const displayStatus = getDisplayStatus(applicant);
        return displayStatus === statusFilter;
      });
    }

    // Filter by selected ratings
    filtered = filtered.filter((applicant) => {
      const rating =
        applicant?.interview_session?.ai_insights?.overall_match_rating;
      // If no rating, check if No Rating is selected
      if (rating === undefined || rating === null) {
        return matchRatingFilter.has("No Rating");
      }
      // Check if rating is in the selected set
      return matchRatingFilter.has(rating);
    });

    // Filter by required questions status
    filtered = filtered.filter((applicant) => {
      const status =
        applicant?.interview_session?.ai_insights?.required_questions_status;
      // If no status, check if NA is selected
      if (status === undefined || status === null) {
        return requiredQuestionsFilter.has("NA");
      }
      // Check if status is in the selected set
      return requiredQuestionsFilter.has(status);
    });

    if (effectiveStageFilter && stageFilterOptionValues.length > 0) {
      filtered = filtered.filter((applicant) => {
        const raw = applicant?.ats_current_stage_name;
        const key =
          typeof raw === "string" && raw.trim() ? raw.trim() : STAGE_FILTER_NA;
        return effectiveStageFilter.has(key);
      });
    }

    if (effectiveStateFilter && stateFilterOptionValues.length > 0) {
      filtered = filtered.filter((applicant) => {
        const raw = applicant?.location?.state;
        const key =
          typeof raw === "string" && raw.trim() ? raw.trim() : STATE_FILTER_NA;
        return effectiveStateFilter.has(key);
      });
    }

    return filtered;
  }, [
    applicantsForMain,
    keywordSearchQuery,
    statusFilter,
    matchRatingFilter,
    requiredQuestionsFilter,
    effectiveStageFilter,
    stageFilterOptionValues,
    effectiveStateFilter,
    stateFilterOptionValues,
  ]);


  // Clear selections when filters change
  useEffect(() => {
    setSelectedApplicants(new Set());
    setSelectedInterviewCandidates(new Set());
  }, [
    keywordSearchQuery,
    statusFilter,
    matchRatingFilter,
    requiredQuestionsFilter,
    effectiveStageFilter,
    effectiveStateFilter,
  ]);

  useEffect(() => {
    if (rejectedApplicants.length === 0) {
      setShowRejectedSection(false);
    }
  }, [rejectedApplicants.length]);

  // Table pagination and sorting
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    currentItems: currentApplicants,
    sortedItems: sortedApplicants,
    sortField,
    sortDirection,
    handlePageChange,
    handleSort,
    resetToFirstPage,
    setCurrentPage,
    setSortField,
    setSortDirection,
  } = useTablePagination(filteredApplicants, APPLICANT_TABLE_PAGINATION_OPTIONS);

  useEffect(() => {
    if (!filtersPersistReadyRef.current) return;
    resetToFirstPage();
  }, [
    keywordSearchQuery,
    statusFilter,
    matchRatingFilter,
    requiredQuestionsFilter,
    resetToFirstPage,
  ]);

  useEffect(() => {
    const saved = savedFiltersRef.current;
    if (
      !filtersPersistReadyRef.current ||
      restoredTablePrefsRef.current ||
      !saved
    ) {
      return;
    }
    if (saved.currentPage) {
      setCurrentPage(saved.currentPage);
    }
    if (saved.sortField) {
      setSortField(saved.sortField);
    }
    if (saved.sortDirection) {
      setSortDirection(saved.sortDirection);
    }
    restoredTablePrefsRef.current = true;
  }, [
    filteredApplicants.length,
    setCurrentPage,
    setSortField,
    setSortDirection,
  ]);

  useEffect(() => {
    if (!jobId || !filtersPersistReadyRef.current) return;
    writeApplicantsListFilters(jobId, {
      statusFilter,
      keywordSearchQuery,
      matchRatingFilter: Array.from(matchRatingFilter),
      requiredQuestionsFilter: Array.from(requiredQuestionsFilter),
      showRejectedSection,
      currentPage,
      sortField,
      sortDirection,
      stage: {
        selected: Array.from(stageFilter),
        knownOptions: stageFilterOptionValues,
        stageUserClearedAll,
      },
      state: {
        selected: Array.from(stateFilter),
        knownOptions: stateFilterOptionValues,
        stateUserClearedAll,
      },
    });
  }, [
    jobId,
    statusFilter,
    keywordSearchQuery,
    matchRatingFilter,
    requiredQuestionsFilter,
    showRejectedSection,
    currentPage,
    sortField,
    sortDirection,
    stageFilter,
    stageFilterOptionKey,
    stageUserClearedAll,
    stateFilter,
    stateFilterOptionKey,
    stateUserClearedAll,
    stageFilterOptionValues,
    stateFilterOptionValues,
  ]);

  useEffect(() => {
    if (totalPages > 0 && currentPage !== totalPages) {
      setShowRejectedSection(false);
    }
  }, [currentPage, totalPages]);

  const {
    currentPage: rejectedCurrentPage,
    itemsPerPage: rejectedItemsPerPage,
    totalPages: rejectedTotalPages,
    currentItems: currentRejectedApplicants,
    sortedItems: sortedRejectedApplicants,
    handlePageChange: handleRejectedPageChange,
  } = useTablePagination(rejectedApplicants, APPLICANT_TABLE_PAGINATION_OPTIONS);

  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // Analysis view state
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'analysis'
  const prevViewModeForImmediatePollRef = useRef("list");
  const [selectedApplicantForAnalysis, setSelectedApplicantForAnalysis] =
    useState(null);
  const {
    isTranscriptLoading,
    transcriptError,
    retryTranscript,
    resetTranscript,
  } = useCandidateTranscript({
    enabled: viewMode === "analysis",
    applicant: selectedApplicantForAnalysis,
    setApplicant: setSelectedApplicantForAnalysis,
    companyId,
    jobId,
    token,
  });

  // Interview action confirmation modal state
  const [interviewActionModalOpen, setInterviewActionModalOpen] =
    useState(false);
  const [candidateForInterviewAction, setCandidateForInterviewAction] =
    useState(null);

  // Bulk interview action state
  const [isBulkInterviewAction, setIsBulkInterviewAction] = useState(false);
  const [bulkInterviewActionType, setBulkInterviewActionType] = useState(null);

  const [isUpdating, setIsUpdating] = useState(false);

  const [calendarLinkSentIds, setCalendarLinkSentIds] = useState(new Set());

  const handleUpdate = useCallback(
    async (silent = false, signal) => {
      setIsUpdating(true);
      try {
        if (silent) {
          return await onCandidatesUpdateSilent(signal);
        }
        return await onCandidatesUpdate(false, signal);
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }
        console.error("Update error:", error);
        if (!silent) {
          toast.error("Update failed, please try again later");
        }
      } finally {
        setIsUpdating(false);
      }
    },
    [onCandidatesUpdate, onCandidatesUpdateSilent]
  );

  useApplicantsAutoUpdate({
    updateFunction: (signal) => handleUpdate(true, signal),
    enabled: viewMode === "list",
    interval: APPLICANTS_TAB_POLL_INTERVAL_MS,
  });

  useAnalysisAutoUpdate({
    analysisStatus: selectedApplicantForAnalysis?.analysis_status,
    updateFunction: (signal) => handleUpdate(true, signal),
    enabled: viewMode === "analysis",
    interval: APPLICANTS_TAB_POLL_INTERVAL_MS,
  });

  // Immediate silent refresh when entering analysis.
  useEffect(() => {
    const enteringAnalysis =
      viewMode === "analysis" &&
      prevViewModeForImmediatePollRef.current !== "analysis";

    if (enteringAnalysis) {
      const analysisStatus = selectedApplicantForAnalysis?.analysis_status;
      const interviewStatus = selectedApplicantForAnalysis?.status;
      const shouldImmediatePoll =
        (interviewStatus === "COMPLETED" && 
          (analysisStatus === "PENDING" ||
          analysisStatus === "IN_PROGRESS" ))||
        interviewStatus === "IN_PROGRESS";

      if (shouldImmediatePoll) {
        void handleUpdate(true);
      }
    }
    prevViewModeForImmediatePollRef.current = viewMode;
  }, [viewMode, handleUpdate, selectedApplicantForAnalysis]);

  useEffect(() => {
    if (!targetSessionId || !applicants?.length) return;
    if (
      viewMode === "analysis" &&
      selectedApplicantForAnalysis?.interview_session_id === targetSessionId
    ) {
      return;
    }
    const applicant = applicants.find(
      (app) => app?.interview_session?.id === targetSessionId
    );

    if (applicant) {
      resetTranscript();
      setSelectedApplicantForAnalysis(toAnalysisApplicant(applicant));
      setViewMode("analysis");
    } else {
      toast.error("Interview session not found or you don't have access to it.");
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [
    targetSessionId,
    applicants,
    viewMode,
    resetTranscript,
    selectedApplicantForAnalysis?.interview_session_id,
  ]);

  useEffect(() => {
    if (
      viewMode !== "analysis" ||
      !selectedApplicantForAnalysis?.id ||
      !applicants?.length
    ) {
      return;
    }
    const updatedApplicant = applicants.find(
      (app) => app?.id === selectedApplicantForAnalysis.id
    );
    if (updatedApplicant) {
      setSelectedApplicantForAnalysis((prev) =>
        mergePreservedTranscript(prev, toAnalysisApplicant(updatedApplicant))
      );
    }
  }, [applicants, selectedApplicantForAnalysis?.id, viewMode]);

  const toggleUploadModal = () => {
    setUploadModalOpen((prev) => !prev);
  };

  const navigateToAnalysis = useCallback(
    (applicantId) => {
      if (
        viewMode === "analysis" &&
        selectedApplicantForAnalysis?.id === applicantId
      ) {
        return;
      }
      const applicant = sortedApplicants.find((a) => a.id === applicantId);
      if (!applicant) return;
      resetTranscript();
      setSelectedApplicantForAnalysis(toAnalysisApplicant(applicant));
      setViewMode("analysis");
    },
    [sortedApplicants, resetTranscript, viewMode, selectedApplicantForAnalysis?.id]
  );

  const [resumePreview, setResumePreview] = useState(null);
  const [analysisTabIndex, setAnalysisTabIndex] = useState(0);
  const analysisPanelBodyRef = useRef(null);

  const navigateBackToList = useCallback(() => {
    setViewMode("list");
    setSelectedApplicantForAnalysis(null);
    setResumePreview(null);
    setAnalysisTabIndex(0);
    resetTranscript();
    navigate(location.pathname, { replace: true });
  }, [navigate, location.pathname, resetTranscript]);

  useEffect(() => {
    setResumePreview(null);
    if (analysisPanelBodyRef.current) {
      analysisPanelBodyRef.current.scrollTop = 0;
    }
  }, [selectedApplicantForAnalysis?.id]);

  const isApplicantAnalysisDisabled = useCallback(
    (applicant) =>
      applicant?.interview_session?.status === "NOT_SENT" ||
      applicant?.status === "NEW" ||
      (applicant?.interview_session?.status === "PENDING" &&
        !(isDevEnvironment() && devModeEnabled)),
    [devModeEnabled]
  );

  const openableApplicants = useMemo(
    () => sortedApplicants.filter((a) => !isApplicantAnalysisDisabled(a)),
    [sortedApplicants, isApplicantAnalysisDisabled]
  );

  const selectedApplicantPosition = useMemo(() => {
    if (viewMode !== "analysis" || !selectedApplicantForAnalysis?.id) return -1;
    return openableApplicants.findIndex(
      (a) => a.id === selectedApplicantForAnalysis.id
    );
  }, [viewMode, selectedApplicantForAnalysis?.id, openableApplicants]);

  const stepSelectedApplicant = useCallback(
    (delta) => {
      if (selectedApplicantPosition < 0) return;
      const nextApplicant = openableApplicants[selectedApplicantPosition + delta];
      if (!nextApplicant) return;
      navigateToAnalysis(nextApplicant.id);
      const sortedIndex = sortedApplicants.findIndex(
        (a) => a.id === nextApplicant.id
      );
      const page = Math.floor(sortedIndex / itemsPerPage) + 1;
      if (sortedIndex >= 0 && page !== currentPage) setCurrentPage(page);
    },
    [
      selectedApplicantPosition,
      openableApplicants,
      navigateToAnalysis,
      sortedApplicants,
      itemsPerPage,
      currentPage,
      setCurrentPage,
    ]
  );

  useEffect(() => {
    if (viewMode !== "analysis" || !showApplicantNavigation) return undefined;
    const handleKeyDown = (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key !== "j" && key !== "k") return;
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('input, textarea, select, [contenteditable="true"]')
      ) {
        return;
      }
      if (document.querySelector('[data-testid="modal-overlay"]')) return;
      event.preventDefault();
      stepSelectedApplicant(key === "j" ? 1 : -1);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [viewMode, showApplicantNavigation, stepSelectedApplicant]);

  const getCandidatesWithClosedInterviews = useCallback(
    (candidateIds) => {
      return candidateIds.filter((id) => {
        const candidate = applicants.find((app) => app.id === id);
        return (
          candidate &&
          candidate.interview_session &&
          candidate.interview_session.id &&
          candidate.interview_session.is_closed
        );
      });
    },
    [applicants]
  );

  const getCandidatesWithOpenInterviews = useCallback(
    (candidateIds) => {
      return candidateIds.filter((id) => {
        const candidate = applicants.find((app) => app.id === id);
        return (
          candidate &&
          candidate.interview_session &&
          candidate.interview_session.id &&
          !candidate.interview_session.is_closed &&
          (candidate.interview_session.status === "PENDING" ||
            candidate.interview_session.status === "IN_PROGRESS")
        );
      });
    },
    [applicants]
  );

  const openBulkCloseInterviewModal = useCallback(() => {
    const eligibleOpenCandidates = getCandidatesWithOpenInterviews(
      Array.from(selectedApplicants)
    );
    const eligibleClosedCandidates = getCandidatesWithClosedInterviews(
      Array.from(selectedApplicants)
    );

    if (
      eligibleOpenCandidates.length > 0 ||
      eligibleClosedCandidates.length > 0
    ) {
      setIsBulkInterviewAction(true);
      // If only open interviews are selected, set type to 'close', otherwise 'reopen'
      setBulkInterviewActionType(
        eligibleOpenCandidates.length > 0 &&
          eligibleClosedCandidates.length === 0
          ? INTERVIEW_ACTION_TYPES.CLOSE
          : INTERVIEW_ACTION_TYPES.REOPEN
      );
      setInterviewActionModalOpen(true);
    }
  }, [
    selectedApplicants,
    getCandidatesWithOpenInterviews,
    getCandidatesWithClosedInterviews,
  ]);

  // Helper function to get candidates that can be downloaded (same logic as clickable applicants)
  const getCandidatesWithCompletedAnalysis = useCallback(
    (candidateIds) => {
      return candidateIds.filter((id) => {
        const candidate = applicants.find((app) => app.id === id);
        // Can download if applicant is clickable (same disabled logic as Link in the table)
        return (
          candidate &&
          candidate.status !== "NEW" &&
          candidate.interview_session &&
          candidate.interview_session.status !== "PENDING" &&
          candidate.interview_session.status !== "NOT_SENT"
        );
      });
    },
    [applicants]
  );

  // Helper function to check if download limit is exceeded
  const getEligibleRetryApplicants = useCallback(
    (candidateIds) => {
      return candidateIds
        .map((id) => applicants.find((app) => app.id === id))
        .filter(
          (applicant) =>
            applicant && isAnalysisRetryEligible(applicant, { isJobClosed })
        );
    },
    [applicants, isJobClosed]
  );

  const getRunnableRetryApplicants = useCallback(
    (candidateIds) => {
      return getEligibleRetryApplicants(candidateIds).filter((applicant) =>
        canRetryAnalysis(applicant, { isJobClosed, job: jobStageSettings })
      );
    },
    [getEligibleRetryApplicants, isJobClosed, jobStageSettings]
  );

  const openRetryAnalysisModal = useCallback(
    (sessionIds) => {
      const uniqueIds = [...new Set(sessionIds.filter(Boolean))];
      if (uniqueIds.length === 0) {
        toast.error("No eligible applicants to retry evaluation");
        return;
      }
      if (uniqueIds.length > RETRY_ANALYSIS_BULK_LIMIT) {
        toast.error(
          `You can retry at most ${RETRY_ANALYSIS_BULK_LIMIT} evaluations at once`
        );
        return;
      }
      setRetryAnalysisSessionIds(uniqueIds);
      setRetryAnalysisModalOpen(true);
    },
    []
  );

  const confirmRetryAnalysis = useCallback(
    async (moveAtsStages) => {
      if (retryAnalysisSessionIds.length === 0) return;
      const sessionIds = [...retryAnalysisSessionIds];
      setRetryAnalysisModalOpen(false);
      setRetryAnalysisSessionIds([]);
      const loadingToastId = toast.loading(
        renderRetryProgressToast(0, sessionIds.length)
      );
      const startedIds = [];
      let skipped = 0;
      let lastError = null;
      try {
        for (const sessionId of sessionIds) {
          try {
            const response = await fetch(
              RETRY_ANALYSIS(companyId, jobId, sessionId),
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  move_ats_stages: Boolean(moveAtsStages),
                }),
              }
            );
            if (!response.ok) {
              const data = await response.json().catch(() => ({}));
              throw new Error(
                getApiErrorMessage(data, "Failed to retry evaluation")
              );
            }
            startedIds.push(sessionId);
          } catch (error) {
            skipped += 1;
            lastError = error;
          }
        }

        if (startedIds.length === 0) {
          throw lastError || new Error("Failed to retry evaluation");
        }

        toast.loading(renderRetryProgressToast(0, startedIds.length), {
          id: loadingToastId,
        });

        const summary = await waitForRetryAnalysesToFinish({
          sessionIds: startedIds,
          fetchApplicants: async () => (await handleUpdate(true)) || [],
          onProgress: ({ completed }) => {
            toast.loading(renderRetryProgressToast(completed, startedIds.length), {
              id: loadingToastId,
            });
          },
        });

        if (summary.failed === 0) {
          toast.success(
            formatBulkRetryToast({
              enqueued: summary.completed,
              skipped,
              stage_moves_eligible: moveAtsStages ? summary.completed : 0,
              move_ats_stages: Boolean(moveAtsStages),
            }),
            { id: loadingToastId }
          );
        } else if (summary.completed === 0) {
          toast.error(
            formatPartialRetryToast({
              completed: 0,
              failed: summary.failed,
              skipped,
            }),
            { id: loadingToastId }
          );
        } else {
          toast.warning(
            formatPartialRetryToast({
              completed: summary.completed,
              failed: summary.failed,
              skipped,
            }),
            { id: loadingToastId }
          );
        }
      } catch (error) {
        console.error("Retry analysis error:", error);
        toast.error(error.message || "Failed to retry evaluation", {
          id: loadingToastId,
        });
      }
    },
    [retryAnalysisSessionIds, companyId, jobId, token, handleUpdate]
  );

  const isOverDownloadLimit = useCallback(() => {
    return (
      getCandidatesWithCompletedAnalysis(Array.from(selectedApplicants))
        .length > DOWNLOAD_LIMIT
    );
  }, [selectedApplicants, getCandidatesWithCompletedAnalysis]);

  // Bulk download analysis handler
  const handleBulkDownloadAnalysis = useCallback(async () => {
    const eligibleCandidates = getCandidatesWithCompletedAnalysis(
      Array.from(selectedApplicants)
    );

    if (eligibleCandidates.length === 0) {
      toast.error("No eligible applicants selected for download");
      return;
    }

    setIsDownloading(true);

    try {
      const transcriptsById = await fetchTranscriptsByCandidateId(
        companyId,
        jobId,
        eligibleCandidates,
        token
      );
      const analysisData = eligibleCandidates.map((id) => {
        const applicant = applicants.find((app) => app.id === id);
        return {
          name: applicant.name,
          phone: applicant.phone_number,
          status: applicant.interview_session.status,
          analysis_status: applicant.interview_session.analysis_status,
          created_at: applicant.interview_session.created_at,
          updated_at: applicant.interview_session.updated_at,
          ai_insights: applicant.interview_session.ai_insights,
          interview_session_id: applicant.interview_session.id,
          interview_responses: transcriptsById.get(id) || [],
          is_closed: applicant.interview_session.is_closed,
          job: applicant.job,
        };
      });

      await bulkExportAnalysisToPDF(analysisData, roleName, companyName);
    } catch (error) {
      console.error("Error downloading analysis:", error);
      toast.error("Failed to download evaluation reports");
    } finally {
      setIsDownloading(false);
    }
  }, [
    selectedApplicants,
    applicants,
    getCandidatesWithCompletedAnalysis,
    roleName,
    companyName,
    companyId,
    jobId,
    token,
  ]);

  const downloadCSV = useCallback(() => {
    try {
      const headers = [
        "Name",
        "Phone Number",
        "Stage",
        "Match Rating",
        "Status",
        "Completion Date",
      ];
      const csvContent = applicantsForMain.map((applicant) => [
        applicant.name,
        formatPhoneNumber(applicant?.phone_number),
        applicant?.ats_current_stage_name?.trim()
          ? applicant.ats_current_stage_name
          : "N/A",
        applicant?.interview_session?.ai_insights?.overall_match_rating || 0,
        formatStatus(getDisplayStatus(applicant)),
        new Date(applicant?.created_at).toLocaleDateString("en-US"),
      ]);

      const csvString = [
        headers.join(","),
        ...csvContent.map((row) => row.join(",")),
      ].join("\n");

      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `applicants_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading CSV:", error);
      toast.error("Failed to download CSV file");
    }
  }, [applicantsForMain]);

  const openDeleteModal = useCallback(
    (candidateId, candidateName, fromRejected = false) => {
      setDeleteFromRejected(fromRejected);
      setCandidateToDelete({ id: candidateId, name: candidateName });
      setDeleteModalOpen(true);
    },
    []
  );

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setCandidateToDelete(null);
    setIsBulkDelete(false);
    setDeleteFromRejected(false);
  }, []);

  const openBulkDeleteModal = useCallback(() => {
    if (selectedApplicants.size > 0) {
      setDeleteFromRejected(false);
      setIsBulkDelete(true);
      setDeleteModalOpen(true);
    }
  }, [selectedApplicants.size]);

  const openRejectModal = useCallback((candidateId, candidateName) => {
    setCandidateToReject({ id: candidateId, name: candidateName });
    setIsBulkReject(false);
    setRejectModalOpen(true);
  }, []);

  const openBulkRejectModal = useCallback(() => {
    if (selectedApplicants.size > 0) {
      setCandidateToReject(null);
      setIsBulkReject(true);
      setRejectModalOpen(true);
    }
  }, [selectedApplicants.size]);

  const closeRejectModal = useCallback(() => {
    setRejectModalOpen(false);
    setCandidateToReject(null);
    setIsBulkReject(false);
  }, []);

  const handleRejectConfirm = useCallback(
    async ({ rejection_reason_id, note }) => {
      const candidateIds = isBulkReject
        ? Array.from(selectedApplicants)
        : [candidateToReject.id];

      setIsRejecting(true);
      try {
        const url = REJECT_CANDIDATES(companyId, jobId);

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            candidate_ids: candidateIds,
            rejection_reason_id,
            note,
          }),
        });

        if (response.ok) {
          const result = await response.json();

          if (isBulkReject) {
            setSelectedApplicants(new Set());
          }

          closeRejectModal();

          if (onCandidatesUpdate) {
            await onCandidatesUpdate();
          }

          const message =
            result.success_count === 1
              ? "1 applicant has been rejected."
              : `${result.success_count} applicants have been rejected.`;
          toast.success(message);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to reject applicants");
        }
      } catch (error) {
        console.error("Error rejecting applicants:", error);
        toast.error(error.message || "Failed to reject applicants");
      } finally {
        setIsRejecting(false);
      }
    },
    [
      isBulkReject,
      selectedApplicants,
      candidateToReject,
      companyId,
      jobId,
      token,
      closeRejectModal,
      onCandidatesUpdate,
    ]
  );

  const getEligibleCandidates = useCallback(
    (candidateIds) => {
      return candidateIds.filter((id) => {
        const candidate = applicants.find((app) => app.id === id);
        return candidate && !candidate.interview_session;
      });
    },
    [applicants]
  );

  const openSingleInterviewModal = useCallback((candidateId, candidateName) => {
    if (!candidateId || !candidateName) {
      toast.error("Invalid candidate data");
      return;
    }

    setCandidateToSendInterview({ id: candidateId, name: candidateName });
    setSelectedInterviewCandidates(new Set([candidateId]));
    if (!hasAnalysisRubric) {
      onAnalysisRubricRequired?.();
      return;
    }
    setSendInterviewModalOpen(true);
  }, [hasAnalysisRubric, onAnalysisRubricRequired]);

  const handleSelectAll = useCallback(
    (checked) => {
      if (checked) {
        // Select all filtered applicants across all pages
        const allApplicantIds = new Set(
          filteredApplicants?.map((app) => app.id) || []
        );
        setSelectedApplicants(allApplicantIds);
      } else {
        setSelectedApplicants(new Set());
      }
    },
    [filteredApplicants]
  );

  const handleSelectApplicant = useCallback(
    (applicantId, checked) => {
      const newSelected = new Set(selectedApplicants);
      if (checked) {
        newSelected.add(applicantId);
      } else {
        newSelected.delete(applicantId);
      }
      setSelectedApplicants(newSelected);
    },
    [selectedApplicants]
  );

  const bulkDeleteApplicants = async () => {
    if (selectedApplicants.size === 0) return;

    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const endpoint = `/companies/${companyId}/jobs/${jobId}/candidates`;
      const url = apiUrl + endpoint;

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          candidate_ids: Array.from(selectedApplicants),
        }),
      });

      if (response.ok) {
        const result = await response.json();

        const deletedCount = selectedApplicants.size;

        // Calculate the appropriate page after deletion
        const newPage = calculatePageAfterDeletion(
          deletedCount,
          currentPage,
          totalPages,
          itemsPerPage,
          sortedApplicants.length
        );
        handlePageChange(newPage);

        setSelectedApplicants(new Set());
        closeDeleteModal();

        if (onCandidatesUpdate) {
          await onCandidatesUpdate();
        }

        const message =
          deletedCount === 1
            ? "1 applicant has been deleted successfully."
            : `${deletedCount} applicants have been deleted successfully.`;
        toast.success(message);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to delete applicants");
      }
    } catch (error) {
      console.error("Error deleting applicants:", error);
      const errorMessage =
        selectedApplicants.size === 1
          ? "Failed to delete applicant"
          : "Failed to delete applicants";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const bulkSendInterviews = async () => {
    let candidateIds = [];

    if (selectedInterviewCandidates.size > 0) {
      // Single candidate from dropdown
      candidateIds = Array.from(selectedInterviewCandidates);
    } else {
      // Bulk selection - filter to only eligible applicants
      candidateIds = getEligibleCandidates(Array.from(selectedApplicants));
    }

    if (candidateIds.length === 0) {
      toast.error(
        "No eligible applicants selected. Only applicants who have not received interviews can be sent invites."
      );
      return;
    }

    const selectedPeople = (applicants || []).filter((a) =>
      candidateIds.includes(a.id)
    );
    const outsideEligible = selectedPeople.filter((a) =>
      applicantStageIsOutsideEligible(
        a.ats_current_stage_name,
        jobStageSettings?.interview_eligible_stage_names
      )
    );
    const terminal = selectedPeople.filter((a) =>
      stageLooksTerminal(a.ats_current_stage_name)
    );
    if (outsideEligible.length || terminal.length) {
      const ok = window.confirm(
        "Some selected applicants are outside this role’s interview-eligible stages or look like a late/rejected stage. Manual invites still send. Continue?"
      );
      if (!ok) return;
    }

    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const endpoint = `/companies/${companyId}/jobs/${jobId}/candidates/invite`;
      const url = apiUrl + endpoint;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          candidate_ids: candidateIds,
        }),
      });

      if (response.ok) {
        const result = await response.json();

        if (onCandidatesUpdate) {
          await onCandidatesUpdate();
        }

        // Clear selections and close modal
        setSelectedApplicants(new Set());
        setSelectedInterviewCandidates(new Set());
        setSendInterviewModalOpen(false);

        const sentCount = typeof result.sent === "number" ? result.sent : candidateIds.length;
        const skippedRegion = result.skipped_unsupported_region || 0;
        if (sentCount === 0 && skippedRegion > 0) {
          toast.warning(
            skippedRegion === 1
              ? "Interview not sent. SMS is only supported for numbers in the United States, Canada, US territories, and the Caribbean."
              : `Interviews not sent. ${skippedRegion} applicants are outside the supported SMS regions (United States, Canada, US territories, and the Caribbean).`
          );
        } else if (skippedRegion > 0) {
          toast.success(
            `Interviews sent to ${sentCount} applicant${sentCount === 1 ? "" : "s"}. ${skippedRegion} not sent (unsupported region).`
          );
        } else {
          const message =
            candidateIds.length === 1
              ? "Interview sent successfully"
              : `Interviews sent to ${candidateIds.length} applicants who have not received interviews`;
          toast.success(message);
        }
      } else {
        const errorData = await response.json();
        if (isAnalysisRubricRequiredDetail(errorData.detail)) {
          setSendInterviewModalOpen(false);
          onAnalysisRubricRequired?.();
          return;
        }
        throw new Error(errorData.detail || "Failed to send interviews");
      }
    } catch (error) {
      console.error("Error sending interviews:", error);
      const errorMessage = "Failed to send interviews. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const openResendInterviewModal = useCallback((candidateId, candidateName) => {
    if (!candidateId || !candidateName) {
      toast.error("Invalid candidate data");
      return;
    }

    setCandidateToResendInterview({ id: candidateId, name: candidateName });
    if (!hasAnalysisRubric) {
      onAnalysisRubricRequired?.();
      return;
    }
    setResendInterviewModalOpen(true);
  }, [hasAnalysisRubric, onAnalysisRubricRequired]);

  const resendInterview = async () => {
    if (!candidateToResendInterview) return;

    setIsLoading(true);

    try {
      const url = RESEND_INTERVIEW(
        companyId,
        jobId,
        candidateToResendInterview.id
      );

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();

        if (onCandidatesUpdate) {
          await onCandidatesUpdate();
        }

        setResendInterviewModalOpen(false);
        setCandidateToResendInterview(null);

        toast.success(
          `Interview resent successfully to ${candidateToResendInterview.name}`
        );
      } else {
        const errorData = await response.json();
        if (isAnalysisRubricRequiredDetail(errorData.detail)) {
          setResendInterviewModalOpen(false);
          onAnalysisRubricRequired?.();
          return;
        }
        throw new Error(errorData.detail || "Failed to resend interview");
      }
    } catch (error) {
      console.error("Error resending interview:", error);
      toast.error(
        error.message || "Failed to resend interview. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const bulkSendCalendarLinks = async () => {
    if (selectedApplicants.size === 0) return;

    setIsSendingCalendar(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const endpoint = SEND_CALENDAR_LINK(companyId, jobId);
      const url = apiUrl ? endpoint : endpoint;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          candidate_ids: Array.from(selectedApplicants),
        }),
      });

      if (response.ok) {
        const result = await response.json();

        // Clear selections
        setSelectedApplicants(new Set());

        // Show success message with count
        const message =
          result.success_count === 1
            ? "Calendar link sent successfully"
            : `Calendar link sent to ${result.success_count} applicant${result.success_count !== 1 ? "s" : ""}`;

        // If some failed, show additional info
        if (result.failed_count > 0) {
          toast.success(`${message} (${result.failed_count} failed)`);
        } else {
          toast.success(message);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to send calendar link");
      }
    } catch (error) {
      console.error("Error sending calendar link:", error);
      toast.error(
        error.message || "Failed to send calendar link. Please try again."
      );
    } finally {
      setIsSendingCalendar(false);
    }
  };

  const sendCalendarLinkToSingle = async (applicant) => {
    if (!applicant || !applicant.id) {
      toast.error("Invalid applicant data");
      return;
    }

    setIsSendingCalendar(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const endpoint = SEND_CALENDAR_LINK(companyId, jobId);
      const url = apiUrl ? endpoint : endpoint;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          candidate_ids: [applicant.id],
        }),
      });

      if (response.ok) {
        setCalendarLinkSentIds((prev) => new Set(prev).add(applicant.id));
        toast.success(`Calendar link sent to ${applicant.name}`);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to send calendar link");
      }
    } catch (error) {
      console.error("Error sending calendar link to single applicant:", error);
      toast.error(error.message || "Failed to send calendar link");
    } finally {
      setIsSendingCalendar(false);
    }
  };

  const sendCalendarReminderToSingle = async (applicant) => {
    if (!applicant || !applicant.id) {
      toast.error("Invalid applicant data");
      return;
    }

    setIsSendingReminder(true);

    try {
      const response = await fetch(SEND_CALENDAR_REMINDER(companyId, jobId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          candidate_ids: [applicant.id],
        }),
      });

      if (response.ok) {
        toast.success(`Reminder sent to ${applicant.name}`);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to send reminder");
      }
    } catch (error) {
      console.error("Error sending reminder to single applicant:", error);
      toast.error(error.message || "Failed to send reminder");
    } finally {
      setIsSendingReminder(false);
    }
  };

  const deleteCandidate = async () => {
    if (isBulkDelete) {
      await bulkDeleteApplicants();
      return;
    }

    if (!candidateToDelete) return;

    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const endpoint = `/companies/${companyId}/jobs/${jobId}/candidates/${candidateToDelete.id}`;
      const url = apiUrl + endpoint;

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const isRejectedRow = deleteFromRejected;
        if (isRejectedRow) {
          const newPage = calculatePageAfterDeletion(
            1,
            rejectedCurrentPage,
            rejectedTotalPages,
            rejectedItemsPerPage,
            sortedRejectedApplicants.length
          );
          handleRejectedPageChange(newPage);
        } else {
          const newPage = calculatePageAfterDeletion(
            1,
            currentPage,
            totalPages,
            itemsPerPage,
            sortedApplicants.length
          );
          handlePageChange(newPage);
        }

        if (onCandidatesUpdate) {
          await onCandidatesUpdate();
        }

        closeDeleteModal();
        toast.success(
          `Applicant ${candidateToDelete.name} has been deleted successfully.`
        );
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to delete candidate");
      }
    } catch (error) {
      console.error("Error deleting candidate:", error);
      const errorMessage = "Failed to delete applicant";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateAndCopy = useCallback(() => {
    if (!jobId || jobId === "undefined") {
      toast.error(
        "Please save the job profile before copying the screening link."
      );
      return;
    }

    // Use short_id if available, otherwise fall back to full jobId
    const screeningLink = shortId
      ? `${window.location.origin}/i/${shortId}`
      : `${window.location.origin}/interview/${jobId}/invite?companyId=${companyId}`;

    navigator.clipboard
      .writeText(screeningLink)
      .then(() => {
        toast.success("Screening link copied to clipboard");
      })
      .catch(() => {
        toast.error("Failed to copy link to clipboard");
      });
  }, [jobId, companyId, shortId]);

  const closeInterview = async (applicant) => {
    if (!applicant?.interview_session?.id) {
      toast.error("No interview session found for this applicant");
      return;
    }
    setCandidateForInterviewAction(applicant);
    setInterviewActionModalOpen(true);
  };

  const openInterview = async (applicant) => {
    if (!applicant?.interview_session?.id) {
      toast.error("No interview session found for this applicant");
      return;
    }
    setCandidateForInterviewAction(applicant);
    setInterviewActionModalOpen(true);
  };

  const confirmCloseInterview = async () => {
    setIsLoading(true);
    try {
      const url = CLOSE_INTERVIEW(companyId, jobId);

      let interviewSessionIds;

      if (isBulkInterviewAction) {
        const eligibleCandidates = getCandidatesWithOpenInterviews(
          Array.from(selectedApplicants)
        );
        if (eligibleCandidates.length === 0) return;

        interviewSessionIds = eligibleCandidates.map((id) => {
          const candidate = applicants.find((app) => app.id === id);
          return candidate.interview_session.id;
        });
      } else {
        interviewSessionIds = [
          candidateForInterviewAction.interview_session.id,
        ];
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          candidate_ids: interviewSessionIds,
        }),
      });

      if (response.ok) {
        if (isBulkInterviewAction) {
          const result = await response.json();
          toast.success(
            `Successfully disabled follow-up messages for ${result.closed_count} interviews`
          );
          setSelectedApplicants(new Set());
        } else {
          toast.success(
            `Follow-up messages disabled for ${candidateForInterviewAction.name}`
          );
        }

        setInterviewActionModalOpen(false);
        setCandidateForInterviewAction(null);
        setIsBulkInterviewAction(false);
        setBulkInterviewActionType(null);

        if (onCandidatesUpdate) {
          await onCandidatesUpdate();
        }
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || "Failed to disable follow-up messages"
        );
      }
    } catch (error) {
      console.error("Error disabling follow-up messages:", error);
      const errorMessage =
        error.message || "Failed to disable follow-up messages";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmOpenInterview = async () => {
    setIsLoading(true);
    try {
      const url = REOPEN_INTERVIEW(companyId, jobId);

      let interviewSessionIds;

      if (isBulkInterviewAction) {
        const eligibleCandidates = getCandidatesWithClosedInterviews(
          Array.from(selectedApplicants)
        );
        if (eligibleCandidates.length === 0) return;

        interviewSessionIds = eligibleCandidates.map((id) => {
          const candidate = applicants.find((app) => app.id === id);
          return candidate.interview_session.id;
        });
      } else {
        interviewSessionIds = [
          candidateForInterviewAction.interview_session.id,
        ];
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          candidate_ids: interviewSessionIds,
        }),
      });

      if (response.ok) {
        if (isBulkInterviewAction) {
          const result = await response.json();
          toast.success(
            `Successfully enabled follow-up messages for ${result.reopened_count} interviews`
          );
          setSelectedApplicants(new Set());
        } else {
          toast.success(
            `Follow-up messages enabled for ${candidateForInterviewAction.name}`
          );
        }

        setInterviewActionModalOpen(false);
        setCandidateForInterviewAction(null);
        setIsBulkInterviewAction(false);
        setBulkInterviewActionType(null);

        if (onCandidatesUpdate) {
          await onCandidatesUpdate();
        }
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || "Failed to enable follow-up messages"
        );
      }
    } catch (error) {
      console.error("Error enabling follow-up messages:", error);
      const errorMessage =
        error.message || "Failed to enable follow-up messages";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Set error state for invalid props
  useEffect(() => {
    if (!jobId || jobId === "undefined" || !companyId) {
      setError("Invalid job or company ID");
    } else {
      setError(null);
    }
  }, [jobId, companyId]);

  const completedInterviews = useMemo(() => {
    return (
      applicantsForMain?.filter(
        (applicant) =>
          applicant?.interview_session?.status === "COMPLETED" ||
          applicant?.interview_session?.status === "FEEDBACK"
      ).length || 0
    );
  }, [applicantsForMain]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center h-64">
        <div className="flex justify-center mt-24">
          <Spinner size="xl" color="primary-700" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-4">
        <div className="text-red-600 mb-2">{error}</div>
        {(!jobId || jobId === "undefined") && (
          <div className="text-gray-600 text-small">
            Please save the job profile before viewing applicants.
          </div>
        )}
      </div>
    );
  }

  // List stays mounted; applicant detail opens in a right drawer over it.
  return (
    <ErrorBoundary
      title="Applicants Tab Error"
      description="There was an error loading the applicants tab. Please try refreshing the page."
      showRefreshButton={true}
    >
      <div className="mt-2 mb-4">
        <div className="mb-4 mx-4 flex flex-wrap items-start justify-between gap-3 pt-2">
          <div className="flex flex-wrap items-center gap-3">
            <SmallText weight="bold" className="whitespace-nowrap">
              {completedInterviews} Completed Interview
              {completedInterviews !== 1 ? "s" : ""}
            </SmallText>
            <SmallText weight="medium" className="whitespace-nowrap">
              of {applicantsForMain?.length || 0} Total Applicant
              {applicantsForMain?.length !== 1 ? "s" : ""}
            </SmallText>

            <div className="flex items-center gap-2">
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => handleUpdate(true)}
                leftIcon={<RefreshCw size="1em" />}
                disabled={isLoading || isUpdating}
                className="shrink-0"
              >
                <span className="whitespace-nowrap">
                  {isUpdating ? "Refreshing..." : "Refresh"}
                </span>
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="tertiary"
              onClick={handleNavigateAndCopy}
              leftIcon={<Copy size="1em" />}
              disabled={isJobClosed}
              className="shrink-0"
            >
              <span className="whitespace-nowrap">Copy Interview Link</span>
            </Button>
            <Dropdown
              placement="bottom-end"
              items={[
                {
                  label: "Upload Applicants",
                  onClick: toggleUploadModal,
                  disabled: !jobId || jobId === "undefined" || isJobClosed,
                  variant: "normal",
                  dataCy: "upload-applicants-button",
                },
                {
                  label: "Download CSV",
                  onClick: downloadCSV,
                  disabled: false,
                  variant: "normal",
                },
              ]}
            />
          </div>
        </div>

        <div className="mb-4 mx-4 flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-4">
            {filteredApplicants?.length > 0 && (
              <>
                <div className="flex shrink-0 items-center gap-2">
                  <Checkbox
                    id="select-all"
                    checked={
                      selectedApplicants.size === filteredApplicants?.length &&
                      filteredApplicants?.length > 0
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                  <XsmallText
                    className="cursor-pointer"
                    weight="bold"
                    onClick={() => {
                      const checkbox = document.getElementById("select-all");
                      if (checkbox) checkbox.click();
                    }}
                  >
                    {selectedApplicants.size === filteredApplicants?.length &&
                    filteredApplicants?.length > 0
                      ? "Deselect All"
                      : "Select All"}
                  </XsmallText>
                </div>
                <SmallText className="shrink-0">|</SmallText>
                <XsmallText weight="bold" className="shrink-0 whitespace-nowrap">
                  {selectedApplicants.size} selected:
                </XsmallText>
                <Button
                  variant="primary"
                  leftIcon={<Send size="1em" />}
                  className="shrink-0"
                  onClick={() => {
                    // Clear single candidate state and open modal for bulk send
                    setCandidateToSendInterview(null);
                    setSelectedInterviewCandidates(new Set());
                    if (!hasAnalysisRubric) {
                      onAnalysisRubricRequired?.();
                      return;
                    }
                    setSendInterviewModalOpen(true);
                  }}
                  disabled={
                    selectedApplicants.size === 0 ||
                    isLoading ||
                    getEligibleCandidates(Array.from(selectedApplicants))
                      .length === 0 ||
                    isJobClosed
                  }
                >
                  <span className="whitespace-nowrap">Send Interviews</span>
                </Button>
                <Button
                  variant="secondary"
                  leftIcon={<Calendar size="1em" />}
                  className="shrink-0"
                  onClick={bulkSendCalendarLinks}
                  disabled={
                    selectedApplicants.size === 0 ||
                    isLoading ||
                    isSendingCalendar
                  }
                >
                  <span className="whitespace-nowrap">
                    {isSendingCalendar ? "Sending..." : "Send Calendar Link"}
                  </span>
                </Button>
                <Button
                  variant="tertiary"
                  leftIcon={<Download size="1em" />}
                  className="shrink-0"
                  onClick={handleBulkDownloadAnalysis}
                  disabled={
                    selectedApplicants.size === 0 ||
                    isLoading ||
                    isDownloading ||
                    getCandidatesWithCompletedAnalysis(
                      Array.from(selectedApplicants)
                    ).length === 0 ||
                    isOverDownloadLimit()
                  }
                  title={isOverDownloadLimit() ? "Exceeds limit" : ""}
                >
                  <span className="whitespace-nowrap">
                    {isDownloading ? "Downloading..." : "Download Evaluation"}
                  </span>
                </Button>
                <Dropdown
                  placement="bottom-end"
                  className="shrink-0"
                  disabled={selectedApplicants.size === 0 || isLoading}
                  items={[
                    {
                      label: "Retry Evaluation",
                      onClick: () => {
                        const runnable = getRunnableRetryApplicants(
                          Array.from(selectedApplicants)
                        );
                        openRetryAnalysisModal(
                          runnable.map((app) => app.interview_session.id)
                        );
                      },
                      disabled:
                        isJobClosed ||
                        getRunnableRetryApplicants(Array.from(selectedApplicants))
                          .length === 0,
                      title: (() => {
                        if (isJobClosed) {
                          return "This job is closed.";
                        }
                        const selected = Array.from(selectedApplicants);
                        const eligible = getEligibleRetryApplicants(selected);
                        const runnable = getRunnableRetryApplicants(selected);
                        if (runnable.length > 0 || eligible.length === 0) {
                          return undefined;
                        }
                        if (eligible.some((applicant) => isAnalysisRetryLocked(applicant))) {
                          return "Evaluation is already running.";
                        }
                        return (
                          getRetryAnalysisDisabledReason(eligible[0], {
                            isJobClosed,
                            job: jobStageSettings,
                          }) || undefined
                        );
                      })(),
                      variant: "normal",
                    },
                    {
                      label:
                        getCandidatesWithClosedInterviews(
                          Array.from(selectedApplicants)
                        ).length > 0 &&
                        getCandidatesWithOpenInterviews(
                          Array.from(selectedApplicants)
                        ).length === 0
                          ? "Enable Follow-up Messages"
                          : "Disable Follow-up Messages",
                      onClick: () => openBulkCloseInterviewModal(),
                      variant:
                        getCandidatesWithClosedInterviews(
                          Array.from(selectedApplicants)
                        ).length > 0 &&
                        getCandidatesWithOpenInterviews(
                          Array.from(selectedApplicants)
                        ).length === 0
                          ? "tertiary"
                          : "danger",
                    },
                    {
                      label: rejectSupported ? "Reject Applicants" : "Reject Applicants (unsupported by your ATS)",
                      onClick: () => openBulkRejectModal(),
                      disabled: !rejectSupported,
                      variant: "danger",
                    },
                    {
                      label: "Delete Applicants",
                      onClick: () => openBulkDeleteModal(),
                      variant: "danger",
                    },
                  ]}
                />
              </>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="show-rejected-applicants"
                data-cy="applicants-show-rejected-checkbox"
                checked={showRejectedSection}
                disabled={rejectedApplicants.length === 0}
                onChange={(e) => {
                  if (rejectedApplicants.length === 0) return;
                  const checked = e.target.checked;
                  if (
                    checked &&
                    totalPages > 0 &&
                    currentPage !== totalPages
                  ) {
                    handlePageChange(totalPages);
                  }
                  setShowRejectedSection(checked);
                }}
              />
              <label
                htmlFor="show-rejected-applicants"
                className={
                  rejectedApplicants.length === 0
                    ? "cursor-not-allowed select-none opacity-50"
                    : "cursor-pointer select-none"
                }
              >
                <XsmallText weight="bold" className="text-inherit">
                  {`Show Rejected (${rejectedApplicants.length})`}
                </XsmallText>
              </label>
            </div>
            <SearchInput
              value={keywordSearchQuery}
              onChange={(e) => setKeywordSearchQuery(e.target.value)}
              onClear={() => setKeywordSearchQuery("")}
              placeholder="Search"
              data-cy="applicants-search-input"
            />
            <FiltersButton
              activeFilterCount={activeFilterCount}
              onClick={() => setIsFiltersDrawerOpen(true)}
              data-cy="applicants-filters-button"
              countDataCy="applicants-filters-count"
            />
          </div>
        </div>

        {activeFilterCount > 0 && (
          <div className="mb-2 mx-4 flex justify-end">
            <XsmallText
              weight="medium"
              className="text-gray-500"
              data-cy="applicants-filtered-count"
            >
              {`Showing ${filteredApplicants.length} ${filteredApplicants.length === 1 ? "Candidate" : "Candidates"}`}
            </XsmallText>
          </div>
        )}

        <Modal
          show={isFiltersDrawerOpen}
          onClose={() => setIsFiltersDrawerOpen(false)}
          placement="right"
          size="md"
          dismissible
        >
          <Modal.Header>Filters</Modal.Header>
          <Modal.Body className="flex flex-col gap-5">
            <ApplicantFiltersDrawerSection title="Status">
              <fieldset className="m-0 border-0 p-0">
                <legend className="sr-only">Status filter</legend>
                <div className="flex flex-col gap-2">
                  {drawerStatusRadioOptions.map((opt) => (
                    <label
                      key={opt.value}
                      className="flex cursor-pointer items-center gap-2"
                    >
                      <input
                        type="radio"
                        name="applicant-status-filter"
                        value={opt.value}
                        checked={statusFilter === opt.value}
                        onChange={() => setStatusFilter(opt.value)}
                        className="h-4 w-4 shrink-0 border-gray-300 text-primary-700 focus:ring-primary-600 dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-primary-600"
                      />
                      <XsmallText weight="medium">{opt.label}</XsmallText>
                    </label>
                  ))}
                </div>
              </fieldset>
            </ApplicantFiltersDrawerSection>
            <ApplicantFiltersDrawerSection title="Match rating">
              <fieldset className="m-0 border-0 p-0">
                <legend className="sr-only">Match rating filter</legend>
                <div className="flex flex-col gap-2">
                  {DRAWER_MATCH_RATING_OPTIONS.map((opt) => (
                    <label
                      key={String(opt.value)}
                      className="flex cursor-pointer items-center gap-2"
                    >
                      <Checkbox
                        id={`drawer-match-${String(opt.value)}`}
                        checked={matchRatingFilter.has(opt.value)}
                        onChange={(e) =>
                          toggleMatchRatingDrawerOption(
                            opt.value,
                            e.target.checked
                          )
                        }
                      />
                      <XsmallText weight="medium" className="cursor-pointer">
                        {opt.label}
                      </XsmallText>
                    </label>
                  ))}
                </div>
              </fieldset>
            </ApplicantFiltersDrawerSection>
            <ApplicantFiltersDrawerSection title="Pass/Fail">
              <fieldset className="m-0 border-0 p-0">
                <legend className="sr-only">Pass/Fail filter</legend>
                <div className="flex flex-col gap-2">
                  {DRAWER_REQUIRED_QUESTIONS_OPTIONS.map((opt) => {
                    const isChecked = opt.value === "FAILED"
                      ? requiredQuestionsFilter.has("PARTIALLY_MET") && requiredQuestionsFilter.has("NOT_MET")
                      : requiredQuestionsFilter.has(opt.value);
                    return (
                    <label
                      key={opt.value}
                      className="flex cursor-pointer items-center gap-2"
                    >
                      <Checkbox
                        id={`drawer-rq-${opt.value}`}
                        checked={isChecked}
                        onChange={(e) =>
                          toggleRequiredQuestionsDrawerOption(
                            opt.value,
                            e.target.checked
                          )
                        }
                      />
                      <XsmallText weight="medium" className="cursor-pointer">
                        {opt.label}
                      </XsmallText>
                    </label>
                    );
                  })}
                </div>
              </fieldset>
            </ApplicantFiltersDrawerSection>
            <ApplicantFiltersDrawerSection title="Stage">
              <fieldset className="m-0 border-0 p-0">
                <legend className="sr-only">Stage filter</legend>
                <SmallText weight="normal" className="mb-2 text-gray-500">
                  This is a view filter only. It does not change who receives automatic interviews.
                </SmallText>
                {stageFilterOptions.length === 0 ? (
                  <SmallText weight="normal" className="text-gray-500">
                    No stages available for this role yet.
                  </SmallText>
                ) : (
                  <div className="flex flex-col gap-2">
                    {stageFilterOptions.map((opt) => {
                      const checked =
                        effectiveStageFilter instanceof Set &&
                        effectiveStageFilter.has(opt.value);
                      const inputId = `drawer-stage-${opt.value}`;
                      return (
                        <label
                          key={opt.value}
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <Checkbox
                            id={inputId}
                            checked={checked}
                            onChange={(e) =>
                              toggleStageDrawerOption(
                                opt.value,
                                e.target.checked
                              )
                            }
                          />
                          <XsmallText
                            weight="medium"
                            className="cursor-pointer"
                          >
                            {opt.label}
                          </XsmallText>
                        </label>
                      );
                    })}
                  </div>
                )}
              </fieldset>
            </ApplicantFiltersDrawerSection>
            <ApplicantFiltersDrawerSection title="State">
              <fieldset className="m-0 border-0 p-0">
                <legend className="sr-only">State filter</legend>
                {stateFilterOptions.length === 0 ? (
                  <SmallText weight="normal" className="text-gray-500">
                    No states available for this role yet.
                  </SmallText>
                ) : (
                  <div className="flex flex-col gap-2">
                    {stateFilterOptions.map((opt) => {
                      const checked =
                        effectiveStateFilter instanceof Set &&
                        effectiveStateFilter.has(opt.value);
                      const inputId = `drawer-state-${opt.value}`;
                      return (
                        <label
                          key={opt.value}
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <Checkbox
                            id={inputId}
                            checked={checked}
                            onChange={(e) =>
                              toggleStateDrawerOption(
                                opt.value,
                                e.target.checked
                              )
                            }
                          />
                          <XsmallText
                            weight="medium"
                            className="cursor-pointer"
                          >
                            {opt.label}
                          </XsmallText>
                        </label>
                      );
                    })}
                  </div>
                )}
              </fieldset>
            </ApplicantFiltersDrawerSection>
            <div className="flex flex-wrap items-center justify-end gap-3 pt-1">
              <Button variant="tertiary" onClick={clearAllFilters}>
                Reset Filters
              </Button>
              <Button
                variant="primary"
                onClick={() => setIsFiltersDrawerOpen(false)}
              >
                Done
              </Button>
            </div>
          </Modal.Body>
        </Modal>

        <div className="overflow-x-auto">
        <Table
          paginated={true}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
        >
          <Table.Head>
            <Table.HeadCell
              sortable={true}
              sortDirection={sortField === "name" ? sortDirection : null}
              onSort={() => handleSort("name")}
            >
              Applicant
            </Table.HeadCell>
            <Table.HeadCell>Phone Number</Table.HeadCell>
            <Table.HeadCell>Location</Table.HeadCell>
            <Table.HeadCell>Stage</Table.HeadCell>
            <Table.HeadCell
              sortable={true}
              sortDirection={
                sortField === "match_rating" ? sortDirection : null
              }
              onSort={() => handleSort("match_rating")}
            >
              Match Rating
            </Table.HeadCell>
            <Table.HeadCell
              sortable={true}
              sortDirection={
                sortField === "required_questions_status" ? sortDirection : null
              }
              onSort={() => handleSort("required_questions_status")}
            >
              Pass/Fail
            </Table.HeadCell>
            <Table.HeadCell>Status</Table.HeadCell>
            <Table.HeadCell>Calendar Link</Table.HeadCell>
            <Table.HeadCell
              sortable={true}
              sortDirection={sortField === "created_at" ? sortDirection : null}
              onSort={() => handleSort("created_at")}
            >
              Created Date
            </Table.HeadCell>
            <Table.HeadCell
              sortable={true}
              sortDirection={sortField === "updated_at" ? sortDirection : null}
              onSort={() => handleSort("updated_at")}
            >
              Last Updated
            </Table.HeadCell>
            <Table.HeadCell className="w-[50px]"></Table.HeadCell>
          </Table.Head>
          <Table.Body
            loading={roleNeedsSetup ? false : loading}
            className="divide-y"
            skeletonRowCount={8}
          >
            {roleNeedsSetup ? (
              <Table.Row>
                <Table.Cell colSpan={11} className="!p-0">
                  <RoleNotSetUpEmptyState onSetUp={onSetUpRole} />
                </Table.Cell>
              </Table.Row>
            ) : !loading && currentApplicants?.map((applicant, index) => {
              const applicationCount = atsApplicationCount(applicant);
              return (
              <Table.Row
                key={index}
                className="bg-white dark:border-gray-700 dark:bg-gray-800"
                selected={
                  viewMode === "analysis" &&
                  selectedApplicantForAnalysis?.id === applicant.id
                }
              >
                <Table.Cell
                  className="whitespace-nowrap font-medium text-gray-900 dark:text-white"
                  data-cy={`applicant-name-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id={`select-${applicant.id}`}
                      checked={selectedApplicants.has(applicant.id)}
                      onChange={(e) =>
                        handleSelectApplicant(applicant.id, e.target.checked)
                      }
                    />
                    <Link
                      to="#"
                      data-side-panel-keep-open
                      onClick={(e) => {
                        e.preventDefault();
                        navigateToAnalysis(applicant.id);
                      }}
                      disabled={isApplicantAnalysisDisabled(applicant)}
                    >
                      {applicant.name}
                    </Link>
                    <OutdatedQuestionsBadge
                      applicant={applicant}
                      companyId={companyId}
                      jobId={jobId}
                      token={token}
                      disabled={isJobClosed}
                      onResent={() => onCandidatesUpdate?.()}
                    />
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <XsmallText weight="normal">
                    {formatPhoneNumber(applicant?.phone_number)}
                  </XsmallText>
                </Table.Cell>
                <Table.Cell>
                  <XsmallText weight="normal">
                    {(() => {
                      const loc = applicant?.location;
                      const parts = [loc?.city, loc?.state].filter(Boolean);
                      return parts.length > 0 ? parts.join(", ") : "N/A";
                    })()}
                  </XsmallText>
                </Table.Cell>
                <Table.Cell>
                  <XsmallText weight="normal">
                    {applicant?.ats_current_stage_name?.trim()
                      ? applicant.ats_current_stage_name
                      : "N/A"}
                  </XsmallText>
                </Table.Cell>
                <Table.Cell>
                  {(() => {
                    const display = getOverallRatingDisplay(
                      applicant?.interview_session?.ai_insights
                    );
                    return display.kind === "rating" ? (
                      <MatchRating rating={display.value} />
                    ) : (
                      <XsmallText weight="normal">{display.value}</XsmallText>
                    );
                  })()}
                </Table.Cell>
                <Table.Cell>
                  <XsmallText weight="normal">
                    {getRequiredQuestionsLabel(
                      applicant?.interview_session?.ai_insights
                        ?.required_questions_status
                    )}
                  </XsmallText>
                </Table.Cell>
                <Table.Cell data-cy={`applicant-status-${index}`}>
                  <Badge status={getDisplayStatus(applicant)}>
                    {formatStatus(getDisplayStatus(applicant))}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex items-center gap-2">
                    {applicant?.calendar_link_sent_at ||
                    calendarLinkSentIds.has(applicant.id) ? (
                      <>
                        <Button
                          variant="ghost"
                          onClick={() =>
                            sendCalendarLinkToSingle(applicant)
                          }
                          disabled={isSendingCalendar || isJobClosed}
                        >
                          Resend Calendar Link
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() =>
                            sendCalendarReminderToSingle(applicant)
                          }
                          disabled={isSendingReminder || isJobClosed}
                        >
                          Send Reminder
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          sendCalendarLinkToSingle(applicant)
                        }
                        disabled={isSendingCalendar || isJobClosed}
                      >
                        Send Calendar Link
                      </Button>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  {applicant?.created_at
                    ? new Date(
                        applicant?.created_at
                      ).toLocaleDateString("en-US")
                    : "N/A"}
                </Table.Cell>
                <Table.Cell>
                  {applicant?.interview_session?.updated_at
                    ? new Date(
                        applicant?.interview_session?.updated_at
                      ).toLocaleDateString("en-US")
                    : "No Interview"}
                </Table.Cell>
                <Table.Cell>
                  <Dropdown
                    placement="bottom-end"
                    id={`dropdown-${applicant.id}`}
                    disabled={selectedApplicants.size > 0}
                    items={[
                      // Dynamically show "Send Interview" or "Resend Interview"
                      ...(applicant?.interview_session
                        ? [
                            {
                              label: "Resend Interview",
                              onClick: () =>
                                openResendInterviewModal(
                                  applicant.id,
                                  applicant.name
                                ),
                              disabled: isJobClosed,
                              variant: "normal",
                            },
                          ]
                        : [
                            {
                              label: "Send Interview",
                              onClick: () =>
                                openSingleInterviewModal(
                                  applicant.id,
                                  applicant.name
                                ),
                              disabled: isJobClosed,
                              variant: "normal",
                            },
                          ]),
                      ...(applicant?.interview_session?.is_closed
                        ? [
                            {
                              label: "Enable Follow-up Messages",
                              onClick: () => openInterview(applicant),
                              disabled: !applicant?.interview_session?.id,
                              variant: "normal",
                            },
                          ]
                        : [
                            {
                              label: "Disable Follow-up Messages",
                              onClick: () => closeInterview(applicant),
                              disabled:
                                !applicant?.interview_session?.id ||
                                !(
                                  applicant?.interview_session?.status ===
                                    "PENDING" ||
                                  applicant?.interview_session?.status ===
                                    "IN_PROGRESS"
                                ),
                              variant: "danger",
                            },
                          ]),
                      {
                        label: "Send Calendar Link",
                        onClick: () => sendCalendarLinkToSingle(applicant),
                        disabled: isSendingCalendar,
                        variant: "normal",
                      },
                      ...(isAnalysisRetryEligible(applicant, { isJobClosed: false })
                        ? [
                            {
                              label: "Retry Evaluation",
                              onClick: () =>
                                openRetryAnalysisModal([
                                  applicant.interview_session.id,
                                ]),
                              disabled: !canRetryAnalysis(applicant, {
                                isJobClosed,
                                job: jobStageSettings,
                              }),
                              title: !canRetryAnalysis(applicant, {
                                isJobClosed,
                                job: jobStageSettings,
                              })
                                ? getRetryAnalysisDisabledReason(applicant, {
                                    isJobClosed,
                                    job: jobStageSettings,
                                  })
                                : undefined,
                              variant: "normal",
                            },
                          ]
                        : []),
                      ...(applicant?.calendar_link_sent_at ||
                      calendarLinkSentIds.has(applicant.id)
                        ? [
                            {
                              label: "Send Reminder",
                              onClick: () =>
                                sendCalendarReminderToSingle(applicant),
                              disabled: isSendingReminder,
                              variant: "normal",
                            },
                          ]
                        : []),
                      ...(applicationCount > 1
                        ? [
                            {
                              label: `Multiple Applications (${applicationCount})`,
                              onClick: () =>
                                setApplicationsModalApplicant(applicant),
                              variant: "normal",
                            },
                          ]
                        : []),
                      {
                        label: rejectSupported ? "Reject" : "Reject (unsupported by your ATS)",
                        onClick: () =>
                          openRejectModal(applicant.id, applicant.name),
                        disabled: applicant.status === "ATS_DECLINED" || !rejectSupported,
                        variant: "danger",
                      },
                      {
                        label: "Delete",
                        onClick: () =>
                          openDeleteModal(applicant.id, applicant.name),
                        variant: "danger",
                      },
                    ]}
                  />
                </Table.Cell>
              </Table.Row>
            );
            })}
            {!roleNeedsSetup && showRejectedSection &&
              rejectedApplicants.length > 0 &&
              (totalPages === 0 || currentPage === totalPages) &&
              currentRejectedApplicants?.map((applicant, index) => {
                const rejectedApplicationCount = atsApplicationCount(applicant);
                return (
                <Table.Row
                  key={`rejected-${applicant.id}`}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <Table.Cell
                    className="whitespace-nowrap font-medium text-gray-900 dark:text-white"
                    data-cy={`rejected-applicant-name-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <XsmallText weight="medium">{applicant.name}</XsmallText>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <XsmallText weight="normal">
                      {formatPhoneNumber(applicant?.phone_number)}
                    </XsmallText>
                  </Table.Cell>
                  <Table.Cell>
                    <XsmallText weight="normal">
                      {(() => {
                        const loc = applicant?.location;
                        const parts = [loc?.city, loc?.state].filter(Boolean);
                        return parts.length > 0 ? parts.join(", ") : "N/A";
                      })()}
                    </XsmallText>
                  </Table.Cell>
                  <Table.Cell>
                    <XsmallText weight="normal">
                      {applicant?.ats_current_stage_name?.trim()
                        ? applicant.ats_current_stage_name
                        : "N/A"}
                    </XsmallText>
                  </Table.Cell>
                  <Table.Cell>
                    {(() => {
                      const display = getOverallRatingDisplay(
                        applicant?.interview_session?.ai_insights
                      );
                      return display.kind === "rating" ? (
                        <MatchRating rating={display.value} />
                      ) : (
                        <XsmallText weight="normal">{display.value}</XsmallText>
                      );
                    })()}
                  </Table.Cell>
                  <Table.Cell>
                    <XsmallText weight="normal">
                      {getRequiredQuestionsLabel(
                        applicant?.interview_session?.ai_insights
                          ?.required_questions_status
                      )}
                    </XsmallText>
                  </Table.Cell>
                  <Table.Cell data-cy={`rejected-applicant-status-${index}`}>
                    <Badge status={getDisplayStatus(applicant)}>
                      {formatStatus(getDisplayStatus(applicant))}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    {applicant?.calendar_link_sent_at ||
                    calendarLinkSentIds.has(applicant.id) ? (
                      <Button
                        variant="ghost"
                        onClick={() =>
                          sendCalendarLinkToSingle(applicant)
                        }
                        disabled={isSendingCalendar || isJobClosed}
                      >
                        Resend Calendar Link
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          sendCalendarLinkToSingle(applicant)
                        }
                        disabled={isSendingCalendar || isJobClosed}
                      >
                        Send Calendar Link
                      </Button>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {applicant?.created_at
                      ? new Date(
                          applicant?.created_at
                        ).toLocaleDateString("en-US")
                      : "N/A"}
                  </Table.Cell>
                  <Table.Cell>
                    {applicant?.interview_session?.updated_at
                      ? new Date(
                          applicant?.interview_session?.updated_at
                        ).toLocaleDateString("en-US")
                      : "No Interview"}
                  </Table.Cell>
                  <Table.Cell>
                    <Dropdown
                      placement="bottom-end"
                      id={`dropdown-rejected-${applicant.id}`}
                      disabled={selectedApplicants.size > 0}
                      items={[
                        ...(rejectedApplicationCount > 1
                          ? [
                              {
                                label: `Multiple Applications (${rejectedApplicationCount})`,
                                onClick: () =>
                                  setApplicationsModalApplicant(applicant),
                                variant: "normal",
                              },
                            ]
                          : []),
                        {
                          label: "Delete",
                          onClick: () =>
                            openDeleteModal(
                              applicant.id,
                              applicant.name,
                              true
                            ),
                          variant: "danger",
                        },
                      ]}
                    />
                  </Table.Cell>
                </Table.Row>
                );
              })}
          </Table.Body>
        </Table>
        </div>

        {!roleNeedsSetup && showRejectedSection &&
          rejectedApplicants.length > 0 &&
          (totalPages === 0 || currentPage === totalPages) &&
          rejectedTotalPages > 1 && (
            <div className="flex justify-end mb-4 mr-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={() =>
                    handleRejectedPageChange(rejectedCurrentPage - 1)
                  }
                  disabled={rejectedCurrentPage === 1}
                >
                  Previous
                </Button>
                <SmallText>
                  Rejected: page {rejectedCurrentPage} of {rejectedTotalPages}
                </SmallText>
                <Button
                  variant="secondary"
                  onClick={() =>
                    handleRejectedPageChange(rejectedCurrentPage + 1)
                  }
                  disabled={rejectedCurrentPage === rejectedTotalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

        <MultipleApplicationsModal
          show={Boolean(applicationsModalApplicant)}
          onClose={() => setApplicationsModalApplicant(null)}
          applicant={applicationsModalApplicant}
        />

        <RejectApplicantModal
          isOpen={rejectModalOpen}
          onClose={closeRejectModal}
          onConfirm={handleRejectConfirm}
          count={isBulkReject ? selectedApplicants.size : 1}
          applicantName={candidateToReject?.name}
          companyId={companyId}
          token={token}
          isLoading={isRejecting}
        />

        <ConfirmationModal
          isOpen={deleteModalOpen}
          onClose={closeDeleteModal}
          onConfirm={deleteCandidate}
          {...getModalConfig(
            "deleteApplicant",
            isBulkDelete ? selectedApplicants.size : 1,
            "Applicant",
            candidateToDelete?.name
          )}
          confirmButtonVariant="danger"
        />

        <ConfirmationModal
          isOpen={sendInterviewModalOpen}
          onClose={() => setSendInterviewModalOpen(false)}
          onConfirm={bulkSendInterviews}
          {...getModalConfig(
            "send",
            selectedInterviewCandidates.size ||
              getEligibleCandidates(Array.from(selectedApplicants)).length,
            "Interview",
            candidateToSendInterview?.name
          )}
          confirmButtonVariant="primary"
        />

        <ConfirmationModal
          isOpen={resendInterviewModalOpen}
          onClose={() => {
            setResendInterviewModalOpen(false);
            setCandidateToResendInterview(null);
          }}
          onConfirm={resendInterview}
          {...getModalConfig(
            "resend",
            1,
            "Interview",
            candidateToResendInterview?.name
          )}
          confirmButtonVariant="danger"
          isLoading={isLoading}
          loadingText="Resending..."
        />

        <UploadApplicantsModal
          isOpen={uploadModalOpen}
          onClose={toggleUploadModal}
          companyId={companyId}
          jobId={jobId}
          token={token}
          onCandidatesUpdate={onCandidatesUpdate}
        />

        <ConfirmationModal
          isOpen={retryAnalysisModalOpen}
          onClose={() => {
            setRetryAnalysisModalOpen(false);
            setRetryAnalysisSessionIds([]);
          }}
          onConfirm={() => confirmRetryAnalysis(false)}
          onSecondaryAction={() => confirmRetryAnalysis(true)}
          secondaryActionVariant="secondary"
          secondaryActionDisabled={!canRetryMoveAtsStages(jobStageSettings)}
          secondaryActionDisabledReason={getAtsStageUpdateDisabledReason(jobStageSettings)}
          cancelButtonVariant="ghost"
          {...getModalConfig(
            "retryAnalysis",
            retryAnalysisSessionIds.length,
            "Applicant",
            retryAnalysisSessionIds.length === 1
              ? applicants.find(
                  (app) =>
                    app.interview_session?.id === retryAnalysisSessionIds[0]
                )?.name
              : undefined
          )}
          confirmButtonVariant="primary"
          loadingText="Retrying..."
        />

        <ConfirmationModal
          isOpen={interviewActionModalOpen}
          onClose={() => {
            setInterviewActionModalOpen(false);
            setCandidateForInterviewAction(null);
            setIsBulkInterviewAction(false);
            setBulkInterviewActionType(null);
          }}
          onConfirm={
            isBulkInterviewAction
              ? bulkInterviewActionType === INTERVIEW_ACTION_TYPES.CLOSE
                ? confirmCloseInterview
                : confirmOpenInterview
              : candidateForInterviewAction?.interview_session?.is_closed
                ? confirmOpenInterview
                : confirmCloseInterview
          }
          {...getModalConfig(
            isBulkInterviewAction
              ? bulkInterviewActionType === INTERVIEW_ACTION_TYPES.CLOSE
                ? "close"
                : "reopen"
              : candidateForInterviewAction?.interview_session?.is_closed
                ? "reopen"
                : "close",
            isBulkInterviewAction
              ? bulkInterviewActionType === INTERVIEW_ACTION_TYPES.CLOSE
                ? getCandidatesWithOpenInterviews(
                    Array.from(selectedApplicants)
                  ).length
                : getCandidatesWithClosedInterviews(
                    Array.from(selectedApplicants)
                  ).length
              : 1,
            "Interview",
            candidateForInterviewAction?.name
          )}
          confirmButtonVariant={
            isBulkInterviewAction
              ? bulkInterviewActionType === INTERVIEW_ACTION_TYPES.CLOSE
                ? "danger"
                : "tertiary"
              : candidateForInterviewAction?.interview_session?.is_closed
                ? "tertiary"
                : "danger"
          }
          isLoading={isLoading}
          loadingText={
            isBulkInterviewAction
              ? bulkInterviewActionType === INTERVIEW_ACTION_TYPES.CLOSE
                ? "Disabling..."
                : "Enabling..."
              : candidateForInterviewAction?.interview_session?.is_closed
                ? "Enabling..."
                : "Disabling..."
          }
        />

        <SidePanel
          show={viewMode === "analysis" && Boolean(selectedApplicantForAnalysis)}
          onClose={navigateBackToList}
          className="right-drawer-applicant"
        >
          <SidePanel.Header
            className="!border-b border-gray-200"
            actions={
              showApplicantNavigation && selectedApplicantPosition >= 0 && (
                <div className="mr-2 flex items-center gap-1">
                  <span className="mr-1 whitespace-nowrap text-body text-gray-500">
                    {`${selectedApplicantPosition + 1} of ${openableApplicants.length}`}
                  </span>
                  <Tooltip content="Previous applicant (K)" position="bottom">
                    <button
                      type="button"
                      aria-label="Previous applicant"
                      onClick={() => stepSelectedApplicant(-1)}
                      disabled={selectedApplicantPosition === 0}
                      className="inline-flex items-center rounded-lg p-1.5 text-gray-400 transition-colors duration-200 hover:bg-gray-200 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                    >
                      <ChevronUp aria-hidden className="h-5 w-5" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Next applicant (J)" position="bottom">
                    <button
                      type="button"
                      aria-label="Next applicant"
                      onClick={() => stepSelectedApplicant(1)}
                      disabled={
                        selectedApplicantPosition === openableApplicants.length - 1
                      }
                      className="inline-flex items-center rounded-lg p-1.5 text-gray-400 transition-colors duration-200 hover:bg-gray-200 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                    >
                      <ChevronDown aria-hidden className="h-5 w-5" />
                    </button>
                  </Tooltip>
                </div>
              )
            }
          >
            {selectedApplicantForAnalysis?.name || "Applicant"}
          </SidePanel.Header>
          <SidePanel.Body
            ref={analysisPanelBodyRef}
            className={
              resumePreview
                ? "flex min-h-0 flex-1 flex-col overflow-hidden p-4"
                : "min-h-0 flex-1 overflow-y-auto pt-2"
            }
          >
            {resumePreview ? (
              <div className="flex min-h-0 flex-1 flex-col gap-3">
                <div className="flex shrink-0 min-w-0 items-center gap-2">
                  <Button
                    variant="ghost"
                    className="!px-2 shrink-0"
                    onClick={() => setResumePreview(null)}
                    leftIcon={<ArrowLeft size="1em" />}
                    aria-label="Back to applicant"
                  >
                    Back
                  </Button>
                  <span className="truncate text-body font-semibold">
                    {resumePreview.title || "Resume preview"}
                  </span>
                </div>
                <ResumePreviewPanel preview={resumePreview} />
              </div>
            ) : (
              selectedApplicantForAnalysis && (
                <ErrorBoundary
                  key={selectedApplicantForAnalysis.id}
                  title="Applicant Evaluation Error"
                  description="There was an error loading the applicant evaluation. Please try refreshing the page."
                  showRefreshButton={true}
                >
                  <div className="side-panel-swap">
                    <ApplicantAnalysisResult
                      presentation="drawer"
                      initialAnalysisTab={analysisTabIndex}
                      onAnalysisTabChange={setAnalysisTabIndex}
                      selectedApplicantForAnalysis={selectedApplicantForAnalysis}
                      navigateBackToList={navigateBackToList}
                      onDataUpdate={(signal) => handleUpdate(true, signal)}
                      onOpenResumePreview={setResumePreview}
                      isUpdating={isUpdating}
                      roleName={roleName}
                      companyName={companyName}
                      companyId={companyId}
                      jobId={jobId}
                      token={token}
                      isTranscriptLoading={isTranscriptLoading}
                      transcriptError={transcriptError}
                      onRetryTranscript={retryTranscript}
                      jobStageSettings={jobStageSettings}
                      hasAnalysisRubric={hasAnalysisRubric}
                      onAnalysisRubricRequired={onAnalysisRubricRequired}
                    />
                  </div>
                </ErrorBoundary>
              )
            )}
          </SidePanel.Body>
        </SidePanel>
      </div>
    </ErrorBoundary>
  );
}
